/* eslint-disable camelcase */
import { useState } from 'react'

import { TextButton } from '@twistopayments/design-system/components/atoms/button'
import { Logo } from '@twistopayments/design-system/components/atoms/logo'
import { Typography } from '@twistopayments/design-system/components/atoms/typography'
import {
  getCallingCodeConfig,
  validator,
} from '@twistopayments/design-system/form'
import { useRouter } from 'next/router'
import { isNil } from 'ramda'

import type { SectionsInvoiceBannerComponent } from '@twisto/api-cms'
import { useAppConfig } from '@twisto/base'

import { useFetchLazy, useHighlightedText } from 'homepage/libs/hooks'

import { Icon } from '../../atoms'
import { Form } from '../../form'

import * as styles from './invoice-banner.styles'

const PLCode = '+48'
const PLConfig = getCallingCodeConfig(PLCode)

export const InvoiceBanner = ({
  title,
  highlighted,
  description,
  features,
  banner,
  cta,
  formName,
  inputs,
  submit,
  errorMessage,
}: SectionsInvoiceBannerComponent) => {
  const { region } = useAppConfig()
  const router = useRouter()
  const { after, before, highlight } = useHighlightedText(title, highlighted)
  const [isInvoiceInvalid, setIsInvoiceInvalid] = useState(false)
  const {
    fetchData: validateInvoiceID,
    error,
    loading,
  } = useFetchLazy<{ valid: boolean }>()

  const handleSubmit = async (formData: Record<string, string>) => {
    const invoiceId = formData.invoice_id || ''
    const email = formData.email || ''
    const phoneNumber = formData.phone_number || ''
    const formattedPhone = phoneNumber.startsWith(PLCode)
      ? phoneNumber.slice(PLCode.length)
      : phoneNumber || ''
    const isPhoneValid =
      !isNil(PLConfig) &&
      validator.isValidPhoneNumber(formattedPhone, PLConfig.format)

    if (
      !invoiceId ||
      (region === 'CZ' ? !validator.isEmail(email) : !isPhoneValid)
    ) {
      setIsInvoiceInvalid(true)

      return
    }

    const regionalData = {
      endpoint:
        region === 'CZ' ? 'invoice/validate/' : 'invoice/validate-phone/',
      body: {
        invoice_id: invoiceId,
        ...(region === 'CZ' ? { email } : { phone_number: phoneNumber }),
      },
    }

    const validationData = await validateInvoiceID({
      method: 'POST',
      url: `${window.location.origin.replace('//www.', '//api.')}/${regionalData.endpoint}`,
      body: JSON.stringify(regionalData.body),
    })

    if (!validationData?.valid) {
      setIsInvoiceInvalid(true)

      return
    }

    router.push(`/app/overview/payment/${invoiceId}`)
  }

  return (
    <section css={styles.section} data-testid="invoice-banner">
      <div css={styles.header}>
        <Typography css={styles.title} fontWeight="heavy" variant="h1">
          {before}
          <span css={styles.highlighted}>{highlight}</span>
          {after}
        </Typography>
        <p css={styles.description}>{description}</p>
      </div>
      <div css={styles.content}>
        <div css={styles.features}>
          {features?.map((feature) => (
            <div key={feature.id} css={styles.feature}>
              <div css={styles.featureHeader}>
                {feature.icon && <Icon name={feature.icon} size="32" />}
                <span>{feature.headline}</span>
              </div>
              <p>{feature.text}</p>
            </div>
          ))}
        </div>
        <div css={styles.formWrapper}>
          <Form
            error={Boolean(error) || isInvoiceInvalid}
            errorMsg={errorMessage}
            formStyles={styles.form}
            inputs={inputs}
            loading={loading}
            name={formName}
            submit={submit}
            submitType="button"
            onSubmit={handleSubmit}
          />
        </div>
      </div>
      {banner && (
        <div css={styles.banner}>
          <Logo inverse logo="twisto" size="large" />
          <span>{banner}</span>
          {cta && (
            <TextButton href={cta.link} variant="inverted">
              {cta.text}
            </TextButton>
          )}
        </div>
      )}
    </section>
  )
}
